import type { FormControl, FormModel, FormNode, FormValue, ItemState } from "./settings-form.ts";

/**
 * The sdkconfig editor's form model: the menu tree of `kconfig_menus.json`,
 * with the values, visibility, ranges and default flags of ESP-IDF's
 * configuration server (kconfserver, protocol version 3). The server is the
 * only judge of dependencies: a change is sent to it, and its reply says what
 * changed, including the symbols the change showed or hid.
 */

/** One entry of `kconfig_menus.json`. */
export interface MenuEntry {
    type: "menu" | "choice" | "comment" | "bool" | "int" | "hex" | "string" | "float";
    id: string;
    name: string | null;
    title: string | null;
    help?: string | null;
    range?: [number, number] | null;
    children: MenuEntry[];
}

/** A kconfserver reply, or the status line: only the entries that changed. */
export interface ServerReply {
    values?: Record<string, FormValue | null>;
    ranges?: Record<string, [number, number]>;
    visible?: Record<string, boolean>;
    defaults?: Record<string, boolean>;
    error?: string[];
}

/** The protocol version the requests are in. */
const VERSION = 3;

export class KconfigModel implements FormModel {
    readonly #menus: MenuEntry[];
    readonly #send: (request: object) => void;
    readonly #values = new Map<string, FormValue | null>();
    readonly #ranges = new Map<string, [number, number]>();
    readonly #visible = new Map<string, boolean>();
    readonly #defaults = new Map<string, boolean>();
    /** Symbols by name, and choices by id, with their menu entry. */
    readonly #entries = new Map<string, MenuEntry>();
    /** A choice member's choice id. */
    readonly #choiceOf = new Map<string, string>();
    readonly #menuIds = new Set<string>();
    readonly #listeners = new Set<(ids: readonly string[] | undefined) => void>();
    #tree: FormNode[] | undefined;

    /** `send` delivers a request to the server; every change is saved to the sdkconfig with it. */
    constructor(menus: MenuEntry[], status: ServerReply, send: (request: object) => void) {
        this.#menus = menus;
        this.#send = send;
        const index = (entries: readonly MenuEntry[], choice: string | undefined): void => {
            for (const entry of entries) {
                if (entry.type === "menu") this.#menuIds.add(entry.id);
                else if (entry.type === "choice") this.#entries.set(entry.id, entry);
                else if (entry.name !== null && entry.type !== "comment") {
                    this.#entries.set(entry.name, entry);
                    if (choice !== undefined) this.#choiceOf.set(entry.name, choice);
                }
                index(entry.children, entry.type === "choice" ? entry.id : undefined);
            }
        };
        index(menus, undefined);
        this.#merge(status);
    }

    tree(): FormNode[] {
        this.#tree ??= this.#nodes(this.#menus);
        return this.#tree;
    }

    state(id: string): ItemState {
        const entry = this.#entries.get(id);
        if (entry?.type === "choice") {
            const members = this.#members(entry);
            const selected = members.find((member) => this.#values.get(member.name!) === true);
            return {
                value: selected?.name ?? "",
                visible: this.#visible.get(id) ?? false,
                modified: members.some((member) => this.#defaults.get(member.name!) === false),
                options: members
                    .filter((member) => this.#visible.get(member.name!) === true)
                    .map((member) => member.name!),
            };
        }
        const value = this.#values.get(id);
        return {
            value: value ?? zero(entry?.type),
            visible: this.#visible.get(id) ?? false,
            modified: this.#defaults.get(id) === false,
        };
    }

    set(id: string, value: FormValue): void {
        const entry = this.#entries.get(id);
        if (entry === undefined) throw new Error(`${id} is not in this configuration.`);
        if (entry.type === "choice") {
            if (typeof value !== "string" || this.#visible.get(value) !== true) {
                throw new Error("That option is not available with the current settings.");
            }
            this.#request({ set: { [value]: true } });
            return;
        }
        const range = this.#ranges.get(id);
        if (typeof value === "number" && range !== undefined && (value < range[0] || value > range[1])) {
            const [low, high] = entry.type === "hex" ? range.map((n) => `0x${n.toString(16)}`) : range;
            throw new Error(`Enter a value from ${low} to ${high}.`);
        }
        if (entry.type === "int" && typeof value === "number" && !Number.isInteger(value)) {
            throw new Error("Enter a whole number.");
        }
        // Shown at once; the reply corrects it and adds what it changed elsewhere.
        this.#values.set(id, value);
        this.#request({ set: { [id]: value } });
    }

    /**
     * The form item that holds the symbol `name`: the symbol's own, or its
     * choice's; "unknown", or "no prompt" for a symbol that only other options set.
     */
    itemOf(name: string): string | { missing: "unknown" | "no prompt" } {
        const entry = this.#entries.get(name);
        if (entry === undefined || entry.type === "choice") return { missing: "unknown" };
        if (entry.title === null) return { missing: "no prompt" };
        return this.#choiceOf.get(name) ?? name;
    }

    reset(id: string): void {
        this.#request({ reset: [id] });
    }

    onChange(listener: (ids: readonly string[] | undefined) => void): () => void {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }

    /** Take in a reply from the server and report what it changed. */
    update(reply: ServerReply): void {
        const changed = this.#merge(reply);
        if (changed === undefined) this.#tree = undefined;
        if (changed !== undefined && changed.length === 0) return;
        for (const listener of this.#listeners) listener(changed);
    }

    #request(parts: object): void {
        this.#send({ version: VERSION, ...parts, save: null });
    }

    /** Merge a reply; the ids whose state changed, or undefined when a menu was shown or hidden. */
    #merge(reply: ServerReply): string[] | undefined {
        const ids = new Set<string>();
        let menus = false;
        const note = (name: string) => {
            ids.add(name);
            const choice = this.#choiceOf.get(name);
            if (choice !== undefined) ids.add(choice);
        };
        for (const [name, value] of Object.entries(reply.values ?? {})) {
            this.#values.set(name, value);
            note(name);
        }
        for (const [name, range] of Object.entries(reply.ranges ?? {})) this.#ranges.set(name, range);
        for (const [name, visible] of Object.entries(reply.visible ?? {})) {
            this.#visible.set(name, visible);
            if (this.#menuIds.has(name)) menus = true;
            note(name);
        }
        for (const [name, isDefault] of Object.entries(reply.defaults ?? {})) {
            this.#defaults.set(name, isDefault);
            note(name);
        }
        return menus ? undefined : [...ids];
    }

    #members(choice: MenuEntry): MenuEntry[] {
        return choice.children.filter((child) => child.name !== null && child.type === "bool");
    }

    /** Menus become groups, symbols and choices items; menus shown by no symbol are left out. */
    #nodes(entries: readonly MenuEntry[]): FormNode[] {
        const nodes: FormNode[] = [];
        for (const entry of entries) {
            if (entry.type === "comment" || entry.title === null) continue;
            if (entry.type === "menu") {
                if (this.#visible.get(entry.id) !== true) continue;
                nodes.push({ kind: "group", id: entry.id, title: entry.title, children: this.#nodes(entry.children) });
                continue;
            }
            const id = entry.type === "choice" ? entry.id : entry.name;
            if (id === null) continue;
            const control = this.#control(entry);
            if (control === undefined) continue;
            // A menu under a symbol, as a `menuconfig` symbol's, follows it: the form nests items only.
            const children = this.#nodes(entry.type === "choice" ? [] : entry.children);
            nodes.push({
                kind: "item",
                id,
                title: entry.title,
                description: entry.help ?? "",
                control,
                // A choice is found by its members' names.
                keywords:
                    entry.type === "choice"
                        ? this.#members(entry)
                              .map((member) => `CONFIG_${member.name}`)
                              .join(" ")
                        : `CONFIG_${entry.name}`,
                children: children.filter((child) => child.kind === "item"),
            });
            nodes.push(...children.filter((child) => child.kind === "group"));
        }
        return nodes;
    }

    #control(entry: MenuEntry): FormControl | undefined {
        switch (entry.type) {
            case "bool":
                return { type: "boolean" };
            case "string":
                return { type: "string" };
            case "int":
                return { type: "number", integer: true };
            case "hex":
                return { type: "number", integer: true, base: 16 };
            case "float":
                return { type: "number" };
            case "choice":
                return {
                    type: "enum",
                    options: this.#members(entry).map((member) => ({
                        value: member.name!,
                        label: member.title ?? member.name!,
                        ...(member.help ? { description: member.help } : {}),
                    })),
                };
            default:
                return undefined;
        }
    }
}

function zero(type: MenuEntry["type"] | undefined): FormValue {
    if (type === "bool") return false;
    if (type === "string") return "";
    return 0;
}
